import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import {
  MessageCircle, X, Send, Loader2, Search, ClipboardCheck,
  Target, FileText, Users, ShieldCheck, ChevronDown, Trash2, Plus, Mic, MicOff, AudioLines
} from "lucide-react";

const QUICK_ACTIONS = [
  { label: "Gap Assessment", icon: Search, action: "I want to start a gap assessment" },
  { label: "Schedule Audit", icon: ClipboardCheck, action: "I need to schedule an audit" },
  { label: "Compliance Status", icon: ShieldCheck, action: "Check my compliance status" },
  { label: "Assign Task", icon: Target, action: "I need to assign a task" },
  { label: "Standards Info", icon: FileText, action: "What standards do you support?" },
  { label: "Talk to Specialist", icon: Users, action: "I want to talk to a human specialist" },
];

const CharstanChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [handoffTriggered, setHandoffTriggered] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const sendMessageRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (text) => {
    if (!text.trim() || sending) return;

    const userMsg = { role: "user", content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setSending(true);
    setShowQuickActions(false);
    setSuggestions([]);

    try {
      const res = await axios.post(`${API}/assistant/chat`, {
        message: text.trim(),
        session_id: sessionId,
      }, { withCredentials: true });

      const assistantMsg = { role: "assistant", content: res.data.response };
      setMessages(prev => [...prev, assistantMsg]);
      setSessionId(res.data.session_id);
      setSuggestions(res.data.suggestions || []);

      if (res.data.needs_handoff) {
        setHandoffTriggered(true);
        setMessages(prev => [...prev, {
          role: "system",
          content: "Connecting you to a CHARSTAN compliance specialist. A team member will be with you shortly."
        }]);
        setSuggestions([]);
      }
    } catch (e) {
      console.error("Chat error:", e);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I'm having trouble connecting. Please try again in a moment."
      }]);
    }
    setSending(false);
  };

  // Keep ref updated so voice callback always has the latest sendMessage
  sendMessageRef.current = sendMessage;

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const transcribeAudio = useCallback(async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");
      const res = await axios.post(`${API}/assistant/voice-to-text`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = res.data.text || "";
      if (text) {
        // Auto-send the transcribed message directly to CHARSTAN AI
        sendMessageRef.current(text);
      } else {
        setMessages(prev => [...prev, {
          role: "system",
          content: res.data.message || "No speech detected. Please try again.",
        }]);
      }
    } catch (e) {
      console.error("Transcription error:", e);
      setMessages(prev => [...prev, {
        role: "system",
        content: "Voice transcription failed. Please check your microphone and try again.",
      }]);
    }
    setIsTranscribing(false);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType.split(";")[0] });
        if (blob.size > 100) {
          transcribeAudio(blob);
        }
      };

      recorder.start(250);
      setIsRecording(true);
    } catch (e) {
      console.error("Mic access error:", e);
      setMessages(prev => [...prev, {
        role: "system",
        content: "Microphone access denied. Please allow microphone permissions in your browser.",
      }]);
    }
  }, [transcribeAudio]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
    setSuggestions([]);
    setShowQuickActions(true);
    setHandoffTriggered(false);
  };

  return (
    <>
      {/* Floating Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 z-[9999] w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105"
          style={{ background: "linear-gradient(135deg, #0D1B2A, #1B3A5C)" }}
          data-testid="chat-toggle-btn"
          aria-label="Open chat assistant"
        >
          <MessageCircle size={24} color="#C9A84C" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-[9999] w-[380px] max-h-[600px] flex flex-col rounded-lg overflow-hidden shadow-2xl"
          style={{ border: "1px solid rgba(201,168,76,0.2)" }}
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between shrink-0"
            style={{ background: "#0D1B2A" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
                style={{ background: "rgba(201,168,76,0.15)", color: "#C9A84C" }}>
                C
              </div>
              <div>
                <h3 className="text-white text-sm font-semibold leading-tight">CHARSTAN Assistant</h3>
                <span className="text-[10px] leading-tight" style={{ color: "#C9A84C" }}>
                  QMS Intelligence
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={startNewSession}
                className="p-1.5 rounded hover:bg-white/10 transition-colors"
                title="New conversation"
                data-testid="chat-new-session-btn">
                <Plus size={14} color="#8B95A5" />
              </button>
              <button onClick={() => setIsOpen(false)}
                className="p-1.5 rounded hover:bg-white/10 transition-colors"
                data-testid="chat-close-btn">
                <X size={16} color="#8B95A5" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "#111318", minHeight: "320px", maxHeight: "420px" }}>
            {/* Welcome Message */}
            {messages.length === 0 && (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ background: "rgba(201,168,76,0.1)" }}>
                  <MessageCircle size={22} color="#C9A84C" />
                </div>
                <h4 className="text-white text-sm font-semibold mb-1">How can I help you today?</h4>
                <p className="text-[#8B95A5] text-xs leading-relaxed max-w-[260px] mx-auto">
                  I'm your QMS compliance advisor. Ask me about standards, gap assessments, audits, or any quality management topic.
                </p>
              </div>
            )}

            {/* Quick Actions */}
            {showQuickActions && messages.length === 0 && (
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="quick-actions">
                {QUICK_ACTIONS.map((qa, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(qa.action)}
                    className="flex items-center gap-2 p-2.5 rounded text-left text-xs transition-all hover:bg-[rgba(201,168,76,0.1)]"
                    style={{ background: "#1A1D26", border: "1px solid rgba(255,255,255,0.06)" }}
                    data-testid={`quick-action-${i}`}
                  >
                    <qa.icon size={14} style={{ color: "#C9A84C" }} />
                    <span className="text-[#CBD5E1]">{qa.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Message Bubbles */}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "system" ? (
                  <div className="w-full p-3 rounded-lg text-center" data-testid={`chat-msg-system-${i}`}
                    style={{ background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.2)" }}>
                    <p className="text-xs font-medium" style={{ color: "#C9A84C" }}>{msg.content}</p>
                  </div>
                ) : (
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-lg text-sm leading-relaxed ${
                      msg.role === "user" ? "text-white" : "text-[#CBD5E1]"
                    }`}
                    style={{
                      background: msg.role === "user" ? "#1B3A5C" : "#1A1D26",
                      border: msg.role === "user" ? "1px solid rgba(54,169,225,0.2)" : "1px solid rgba(255,255,255,0.06)",
                    }}
                    data-testid={`chat-msg-${msg.role}-${i}`}
                  >
                    {msg.content}
                  </div>
                )}
              </div>
            ))}

            {/* Typing Indicator */}
            {sending && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-lg" style={{ background: "#1A1D26", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="flex gap-1.5" data-testid="typing-indicator">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="w-2 h-2 rounded-full animate-bounce"
                        style={{ backgroundColor: "#C9A84C", animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Handoff Notice — persistent banner after handoff triggered */}
            {handoffTriggered && (
              <div className="space-y-2">
                <div className="p-2.5 rounded-lg flex items-center gap-2" data-testid="handoff-notice"
                  style={{ background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.15)" }}>
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#C9A84C" }} />
                  <p className="text-[11px]" style={{ color: "#C9A84C" }}>
                    Specialist queue active — you'll be connected shortly
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const lastUserMsg = messages.filter(m => m.role === "user").pop()?.content || "";
                      const res = await fetch(`${API}/crm/support-tickets`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                          subject: lastUserMsg.slice(0, 100) || "AI Chat Escalation",
                          description: messages.filter(m => m.role !== "system").map(m => `[${m.role}] ${m.content}`).join("\n").slice(0, 2000),
                          category: "general",
                          priority: "medium",
                          channel: "ai_chat",
                        }),
                      });
                      if (res.ok) {
                        const tkt = await res.json();
                        setMessages(prev => [...prev, { role: "system", content: `Support ticket created: ${tkt.ticket_id}. Our team will follow up.` }]);
                        setHandoffTriggered(false);
                      }
                    } catch (err) {
                      console.error("Ticket creation failed:", err);
                    }
                  }}
                  className="w-full px-3 py-2 rounded-lg text-[11px] font-medium transition-all hover:opacity-90"
                  style={{ background: "rgba(54,169,225,0.12)", color: "#36A9E1", border: "1px solid rgba(54,169,225,0.2)" }}
                  data-testid="chat-create-ticket-btn"
                >
                  Create Support Ticket
                </button>
              </div>
            )}

            {/* Suggestion Chips */}
            {suggestions.length > 0 && !sending && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="suggestion-chips">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)}
                    className="px-2.5 py-1 rounded-full text-[11px] transition-all hover:bg-[rgba(201,168,76,0.15)]"
                    style={{ background: "rgba(201,168,76,0.08)", color: "#C9A84C", border: "1px solid rgba(201,168,76,0.15)" }}
                    data-testid={`suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="px-3 py-3 shrink-0" style={{ background: "#0D1B2A", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about compliance, standards, audits..."
                rows={1}
                className="flex-1 px-3 py-2.5 rounded-lg text-sm text-white placeholder-[#8B95A5] outline-none resize-none"
                style={{ background: "#1A1D26", border: "1px solid rgba(255,255,255,0.08)", maxHeight: "80px" }}
                data-testid="chat-input"
              />
              <button
                onClick={toggleRecording}
                disabled={isTranscribing}
                className={`p-2.5 rounded-lg transition-all ${isRecording ? "bg-red-500/20 animate-pulse" : isTranscribing ? "bg-[#C9A84C]/20" : "bg-[#1A1D26]"}`}
                style={{ border: `1px solid ${isRecording ? "rgba(239,68,68,0.3)" : isTranscribing ? "rgba(201,168,76,0.3)" : "rgba(255,255,255,0.08)"}` }}
                title={isRecording ? "Stop recording" : isTranscribing ? "Transcribing..." : "Voice input"}
                data-testid="voice-input-btn"
              >
                {isTranscribing ? (
                  <Loader2 size={16} className="animate-spin text-[#C9A84C]" />
                ) : isRecording ? (
                  <AudioLines size={16} className="text-red-400" />
                ) : (
                  <Mic size={16} className="text-[#8B95A5]" />
                )}
              </button>
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || sending}
                className="p-2.5 rounded-lg transition-all disabled:opacity-30"
                style={{ background: input.trim() ? "#C9A84C" : "rgba(201,168,76,0.2)" }}
                data-testid="chat-send-btn"
              >
                {sending ? <Loader2 size={16} className="animate-spin text-[#0D1B2A]" /> : <Send size={16} color="#0D1B2A" />}
              </button>
            </div>
            <p className="text-[9px] text-[#8B95A5] mt-1.5 text-center">
              CHARSTAN AI Assistant — Powered by Claude
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default CharstanChatWidget;
